import React, { useRef, useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { uploadWebsiteBlockImage } from '../../../../services/storageService';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES, IMAGE_SIZE_OPTIONS } from '../common/commonDefaults';
import LinkInput from '../common/LinkInput';

/**
 * Returns extra style controls for the right-side style panel.
 */
export const getTextImageExtraStyleControls = ({ content, onChange }) => ({
  sizeSpacingExtras: (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
        Image
      </h4>
      <div>
        <label className="block text-sm text-gray-300 mb-1">Image Size</label>
        <div className="grid grid-cols-4 gap-1.5">
          {IMAGE_SIZE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange({ imageSize: option.value })}
              className={`py-1.5 rounded text-sm font-medium transition-colors ${
                (content.imageSize || 'medium') === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  ),
});

const TextImageEditor = ({ block, onChange, profileId, theme, hideStylePanels, pages }) => {
  const { content } = block;
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setUploadError('');

    try {
      const downloadURL = await uploadWebsiteBlockImage(file, profileId, block.id);
      handleChange('image', downloadURL);
    } catch (error) {
      setUploadError(error.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    handleChange('image', '');
  };

  const features = BLOCK_FEATURES['text-image'];
  const extras = getTextImageExtraStyleControls({ content, onChange });

  return (
    <div className="space-y-4">
      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Enter heading..."
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Body Text */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Body Text
        </label>
        <textarea
          value={content.body || ''}
          onChange={(e) => handleChange('body', e.target.value)}
          placeholder="Enter your content..."
          rows={5}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none resize-none"
        />
      </div>

      {/* Image */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Image
        </label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleImageUpload}
          className="hidden"
        />

        {content.image ? (
          <div className="relative rounded-lg overflow-hidden">
            <img
              src={content.image}
              alt="Section image"
              className="w-full h-40 object-cover"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 p-1.5 bg-black/60 hover:bg-black/80 rounded-full text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full h-32 border-2 border-dashed border-gray-600 rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-[#749555] hover:text-[#749555] transition-colors disabled:opacity-50"
          >
            {isUploading ? (
              <Loader2 size={24} className="animate-spin" />
            ) : (
              <>
                <Upload size={24} />
                <span className="text-sm mt-2">Upload Image</span>
              </>
            )}
          </button>
        )}

        {uploadError && (
          <p className="text-red-400 text-sm mt-1">{uploadError}</p>
        )}
      </div>

      {/* Image Alt Text */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Image Alt Text
        </label>
        <input
          type="text"
          value={content.imageAlt || ''}
          onChange={(e) => handleChange('imageAlt', e.target.value)}
          placeholder="Describe the image..."
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* Layout (structural — stays inline) */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Layout
        </label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'imageLeft', label: 'Image Left' },
            { value: 'imageRight', label: 'Image Right' },
            { value: 'imageTop', label: 'Image Top' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleChange('layout', option.value)}
              className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                content.layout === option.value
                  ? 'bg-[#749555] text-white'
                  : 'bg-[#1a1a2e] text-gray-400 hover:text-white border border-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Button (Optional) */}
      <div className="border-t border-gray-700 pt-4 mt-4">
        <label className="block text-sm font-medium text-gray-300 mb-3">
          Button (Optional)
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <input
              type="text"
              value={content.buttonText || ''}
              onChange={(e) => handleChange('buttonText', e.target.value)}
              placeholder="Button text..."
              className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none text-sm"
            />
          </div>
          <div>
            <LinkInput
              value={content.buttonLink || ''}
              onChange={(val) => handleChange('buttonLink', val)}
              pages={pages}
            />
          </div>
        </div>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (
        <>
          <SizeSpacingPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={features}
            extraControls={extras.sizeSpacingExtras}
          />
          <ColorsFontsPanel
            style={content.style || {}}
            onChange={handleStyleChange}
            features={features}
            theme={theme || {}}
            blockType="text-image"
          />
        </>
      )}
    </div>
  );
};

export default TextImageEditor;
